/* COLLAPSE · Drop : fiches HUD statiques DOS / FACE (dessin 2D partagé avec la colonne 3D, collapse-hud.js).
   L'animation légère (flottement, reflet diagonal, halo) est en CSS : aucun rendu WebGL ici.
   Les fiches se forment depuis les cendres à leur première apparition. Mobile : une seule fiche qui « collapse » et
   se transforme en l'autre face toutes les 7 s : chaque grain quitte un point de la fiche visible et rejoint un point
   de l'autre face, sa couleur passant de l'une à l'autre (≈ 2 s) ; pastilles DOS / FACE pour basculer. */
import { loadImage, cardSizes, drawCard } from './collapse-hud.js?v=tight2';

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const MOBILE = matchMedia('(max-width: 900px)');
const EVERY = 7000;          // bascule automatique (mobile), comptée depuis la fin de la précédente
const MORPH = 2000;          // durée de la métamorphose
const FORM = 1400;           // première apparition (formation seule)
const rand = (a, b) => a + Math.random() * (b - a);
const clamp01 = (x) => Math.min(1, Math.max(0, x));
const smooth = (x) => { x = clamp01(x); return x * x * (3 - 2 * x); };
const easeInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

function fontsFor() {
  const sub = (getComputedStyle(document.documentElement).getPropertyValue('--font-subheading--family') || '').trim();
  const fonts = { label: (sub ? sub + ', ' : '') + 'sans-serif', name: "GFSDidot, 'CollapseGreek', Georgia, serif" };
  const ready = document.fonts
    ? Promise.all([document.fonts.load('400 34px ' + fonts.label), document.fonts.load('700 40px ' + fonts.label), document.fonts.load('400 64px GFSDidot'), document.fonts.load("400 64px 'CollapseGreek'")]).catch(() => {})
    : Promise.resolve();
  return { fonts, ready };
}

// Grains : couleurs de TOUTE la fiche (cadre, marbre, pièce, textes), parties transparentes écartées.
// Calculés une fois par face (au dessin de la fiche), réutilisés à chaque bascule.
function sample(cv, n) {
  const cols = 72, rows = 90, c = document.createElement('canvas');
  c.width = cols; c.height = rows;
  const g = c.getContext('2d', { willReadFrequently: true });
  g.drawImage(cv, 0, 0, cols, rows);
  const d = g.getImageData(0, 0, cols, rows).data, out = [];
  for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
    const o = (j * cols + i) * 4;
    if (d[o + 3] < 60) continue;
    const k = 0.88;   // légèrement assombri : la cendre reste visible sur le marbre clair
    out.push({ u: (i + Math.random()) / cols, v: (j + Math.random()) / rows, c: [d[o] * k, d[o + 1] * k, d[o + 2] * k] });
  }
  for (let k = out.length - 1; k > 0; k--) { const j = (Math.random() * (k + 1)) | 0; [out[k], out[j]] = [out[j], out[k]]; }
  // appariement cohérent entre les deux faces : ordre de haut en bas (avec du bruit), calculé une fois
  return out.slice(0, n).map((p) => Object.assign(p, { k: p.v + (p.u - 0.5) * 0.2 + Math.random() * 0.14 })).sort((a, b) => a.k - b.k);
}
// Palette réduite : 24 gris chauds (la fiche est en niveaux de gris) + 6 rouges (point du circuit, prix). Les grains
// d'une même teinte sont dessinés d'un seul trait, ce qui divise le travail par image (fluide sur téléphone).
const GREYS = 24, REDS = 6, PAL = [];
for (let i = 0; i < GREYS; i++) { const v = Math.round(i * 235 / (GREYS - 1)); PAL.push(`rgb(${v},${Math.round(v * 0.985)},${Math.round(v * 0.95)})`); }
for (let i = 0; i < REDS; i++) { const v = 0.45 + 0.55 * i / (REDS - 1); PAL.push(`rgb(${Math.round(200 * v)},${Math.round(60 * v)},${Math.round(45 * v)})`); }
const palIdx = (c) => {
  const lum = 0.3 * c[0] + 0.59 * c[1] + 0.11 * c[2];
  if (c[0] - Math.max(c[1], c[2]) > 45) return GREYS + Math.max(0, Math.min(REDS - 1, Math.round((c[0] / 200 - 0.45) / 0.55 * (REDS - 1))));
  return Math.max(0, Math.min(GREYS - 1, Math.round(lum / 235 * (GREYS - 1))));
};
const ALPHAS = [0.35, 0.7, 1];
const buckets = Array.from({ length: PAL.length * ALPHAS.length }, () => []);

// Métamorphose sur une toile posée dans la fiche (elle suit son flottement), un peu plus grande qu'elle.
// from : couche visible (ou null : formation depuis des cendres éparses) ; to : couche qui se forme.
function ashSwap(dc, from, to, dur) {
  return new Promise((done) => {
    const host = dc.querySelector('.clp-dc__float') || dc;
    const W = host.clientWidth, H = host.clientHeight;
    const finish = () => { if (from) { from.classList.remove('is-on'); from.style.opacity = ''; } to.classList.add('is-on'); to.style.opacity = ''; dc.classList.remove('is-swapping'); };
    if (!W || !H || !to.__cv) { finish(); return done(); }
    const PAD = 0.25, pr = Math.min(1.5, devicePixelRatio || 1), CW = W * (1 + 2 * PAD), CH = H * (1 + 2 * PAD);
    const cv = document.createElement('canvas');
    cv.className = 'clp-dc__ash';
    // (max-width : la règle générale du thème « canvas { max-width:100% } » rétrécissait la toile → grains en haut à gauche)
    Object.assign(cv.style, { left: -W * PAD + 'px', top: -H * PAD + 'px', width: CW + 'px', height: CH + 'px', maxWidth: 'none', maxHeight: 'none' });
    cv.width = Math.round(CW * pr); cv.height = Math.round(CH * pr);
    host.appendChild(cv);
    const g = cv.getContext('2d');
    g.scale(pr, pr);
    const ox = W * PAD, oy = H * PAD;
    const N = MOBILE.matches ? 2400 : 3200;
    // les deux faces sont déjà triées de haut en bas : le i-ème grain de l'une rejoint le i-ème de l'autre
    const pick = (arr) => { const out = [], st = arr.length / N; for (let i = 0; i < N && i * st < arr.length; i++) out.push(arr[Math.floor(i * st)]); return out; };
    const B = pick(to.__pts || (to.__pts = sample(to.__cv, 4000)));
    const A = from && from.__cv ? pick(from.__pts || (from.__pts = sample(from.__cv, 4000))) : null;
    const pts = B.map((b, i) => {
      const bx = ox + b.u * W, by = oy + b.v * H;
      let ax, ay, ca;
      if (A) { const a = A[Math.floor(i * A.length / B.length)]; ax = ox + a.u * W; ay = oy + a.v * H; ca = a.c; }
      else { ax = bx + rand(-0.5, 0.5) * W * 0.6; ay = oy + H + rand(0.05, 0.5) * H * 0.5; ca = b.c; }
      const cols = [];
      for (let s = 0; s <= 5; s++) cols.push(palIdx([0, 1, 2].map((q) => ca[q] + (b.c[q] - ca[q]) * s / 5)));
      // départ échelonné : le haut de la fiche se défait en premier, avec du désordre
      const d = (A ? 0.26 : 0.3) * clamp01(b.v * 0.7 + Math.random() * 0.3);
      return { ax, ay, bx, by, cols, d, sw: rand(-1, 1), s: rand(0.9, 1.9) };
    });
    const LIFE = A ? 0.66 : 0.62;
    to.style.opacity = '0';
    to.classList.add('is-on');
    dc.classList.add('is-swapping');
    const t0 = performance.now();
    const step = (now) => {
      const x = (now - t0) / dur;
      g.clearRect(0, 0, CW, CH);
      // la fiche visible s'efface pendant que ses grains s'en détachent ; l'autre se révèle à leur arrivée
      if (from) from.style.opacity = String(1 - 0.92 * smooth((x - 0.12) / 0.5) - 0.08 * smooth((x - 0.62) / 0.2));
      to.style.opacity = String(A ? smooth((x - 0.36) / 0.5) : smooth((x - 0.42) / 0.5));
      for (const p of pts) {
        const s = clamp01((x - p.d) / LIFE);
        if (s <= 0 || s >= 1) continue;
        const e = easeInOut(s), arc = Math.sin(Math.PI * s);
        const al = Math.min(1, s * 8) * (1 - smooth((s - 0.82) / 0.18));
        p.px = p.ax + (p.bx - p.ax) * e + p.sw * arc * W * 0.14;
        p.py = p.ay + (p.by - p.ay) * e - arc * H * 0.05 + Math.cos(Math.PI * s * 2) * p.sw * 3;
        p.pz = p.s * (1 + 0.4 * arc);
        buckets[p.cols[Math.round(e * 5)] * ALPHAS.length + (al > 0.85 ? 2 : al > 0.5 ? 1 : 0)].push(p);
      }
      for (let k = 0; k < buckets.length; k++) {
        const bk = buckets[k];
        if (!bk.length) continue;
        g.globalAlpha = ALPHAS[k % ALPHAS.length];
        g.fillStyle = PAL[(k / ALPHAS.length) | 0];
        g.beginPath();
        for (const p of bk) g.rect(p.px, p.py, p.pz, p.pz);
        g.fill();
        bk.length = 0;
      }
      g.globalAlpha = 1;
      if (x < 1) return requestAnimationFrame(step);
      cv.remove();
      finish();
      done();
    };
    requestAnimationFrame(step);
  });
}

function initDrop(sec) {
  let data;
  try { data = JSON.parse(sec.querySelector('[data-clp-drop-data]').textContent); } catch (e) { return; }
  const cards = [...sec.querySelectorAll('[data-clp-dc]')];
  if (!cards.length) return;
  const { fonts, ready } = fontsFor();
  const hi = (u) => (u && u.startsWith('//') ? location.protocol + u : u);
  // Résolution du dessin : celle de la plus grande fiche affichée, à la densité réelle de l'écran (fiche nette).
  const maxW = Math.max(...cards.map((dc) => dc.getBoundingClientRect().width || 0), 360);
  const S = Math.min(2, Math.max(0.5, (maxW * Math.min(3, devicePixelRatio || 1)) / 1024));

  const views = { dos: { src: data.dos, label: data.lblDos }, face: { src: data.face, label: data.lblFace } };
  if (data.cote) views.cote = { src: data.cote, label: data.lblCote || 'CÔTÉ' };   // vue de côté (fiche double, téléphone)
  const cvs = {};
  const painted = Promise.all([loadImage(data.marble).catch(() => null), ready]).then(([marble]) => Promise.all(Object.entries(views).map(([side, v]) => {
    if (!v.src) return null;
    return loadImage(hi(v.src)).then((img) => new Promise((res) => {
      const idle = window.requestIdleCallback || ((f) => setTimeout(f, 30));
      idle(() => {
        // prix : celui du drop en vente ; avant la sortie, prix d'aperçu sur ordinateur seulement
        const prod = { t: data.t, p: data.p || (MOBILE.matches ? '' : data.pDesk || ''), label: v.label, spec: data.spec || '' };
        try { cvs[side] = drawCard(prod, 0, 1, img, marble, fonts, cardSizes([prod], fonts), S); } catch (e) { console.error('[drop]', e); }
        res();
      }, { timeout: 800 });
    })).catch(() => null);
  })));

  const state = cards.map((dc) => ({ dc, layers: [...dc.querySelectorAll('.clp-dc__l')], shown: false, onScreen: false }));
  const layerOf = (st, side) => st.layers.find((l) => l.dataset.side === side);
  const current = (st) => st.layers.find((l) => l.classList.contains('is-on')) || st.layers[0];
  const visible = (st) => st.dc.getClientRects().length > 0;

  // Première apparition : chaque fiche visible se forme depuis les cendres quand elle entre à l'écran.
  function reveal(st) {
    if (st.shown || !st.painted || !st.onScreen || !visible(st)) return;
    st.shown = true;
    st.dc.classList.add('is-ready');
    if (REDUCED) { current(st).style.opacity = ''; return; }
    st.busy = true;
    ashSwap(st.dc, null, current(st), FORM).then(() => { st.busy = false; st.lastSwap = performance.now(); });
  }

  painted.then(() => {
    state.forEach((st) => {
      st.layers.forEach((l) => {
        const c = cvs[l.dataset.side];
        if (!c) return;
        // chaque fiche a sa propre copie (une toile ne peut être affichée qu'à un seul endroit)
        const own = document.createElement('canvas');
        own.width = c.width; own.height = c.height;
        own.getContext('2d').drawImage(c, 0, 0);
        own.setAttribute('aria-hidden', 'true');
        l.__cv = own;
        l.appendChild(own);
        (window.requestIdleCallback || ((f) => setTimeout(f, 50)))(() => { if (!l.__pts) l.__pts = sample(own, 4000); });
        l.classList.add('is-painted');
      });
      st.painted = st.layers.some((l) => l.__cv);
      if (!st.painted) return;
      if (!REDUCED && !st.shown) current(st).style.opacity = '0';   // cachée jusqu'à sa formation
      reveal(st);
    });
  });

  const io = new IntersectionObserver((en) => en.forEach((e) => {
    const st = state.find((s) => s.dc === e.target);
    if (!st) return;
    st.onScreen = e.isIntersecting;
    st.dc.classList.toggle('is-idle', !e.isIntersecting);
    if (e.isIntersecting) reveal(st);
  }), { threshold: 0.35 });
  state.forEach((st) => io.observe(st.dc));

  // ===== Mobile : fiche double (dos ↔ face) =====
  const dual = state.find((st) => st.dc.dataset.clpDc === 'dual');
  const pills = [...sec.querySelectorAll('.clp-dc__pills [data-to]')];
  let queued = null;   // appui pendant une bascule : exécuté juste après
  const setPills = (side) => pills.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.to === side)));
  async function show(side, instant) {
    if (!dual) return;
    if (dual.busy) { queued = side; setPills(side); return; }
    const from = current(dual), to = layerOf(dual, side);
    if (!to || from === to) return;
    setPills(side);
    if (instant || REDUCED || !dual.shown || !to.__cv) { from.classList.remove('is-on'); from.style.opacity = ''; to.classList.add('is-on'); to.style.opacity = ''; dual.lastSwap = performance.now(); return; }
    dual.busy = true;
    await ashSwap(dual.dc, from, to, MORPH);
    dual.busy = false;
    dual.lastSwap = performance.now();
    if (queued) { const q = queued; queued = null; if (q !== current(dual).dataset.side) show(q); else setPills(q); }
  }
  pills.forEach((b) => b.addEventListener('click', () => show(b.dataset.to)));
  // Téléphone : la fiche double montre d'abord la face, puis bascule sur le dos.
  if (dual && MOBILE.matches) show('face', true);
  // Ordinateur : la fiche de gauche montre toujours le dos.
  MOBILE.addEventListener('change', () => { if (!MOBILE.matches) show('dos', true); else if (dual) reveal(dual); });
  if (dual && !REDUCED) {
    setInterval(() => {
      if (!MOBILE.matches || dual.busy || document.hidden || !dual.onScreen || !dual.shown) return;
      if (performance.now() - (dual.lastSwap || 0) < EVERY) return;
      // ordre de la bascule : face → dos → côté (les vues présentes seulement)
      const order = ['face', 'dos', 'cote'].filter((sd) => { const l = layerOf(dual, sd); return l && l.__cv; });
      if (order.length < 2) return;
      show(order[(order.indexOf(current(dual).dataset.side) + 1) % order.length]);
    }, 250);
  }

  // Ordinateur : bloc resserré autour de DROP I (centré) — signature + titre au-dessus et compte à rebours dessous, à la
  // même distance ; les boutons restent en bas.
  const bk = sec.querySelector('.clp-bk');
  function placeCountdown() {
    const cd = bk && bk.querySelector('.clp-c--cd'), drop = bk && bk.querySelector('.clp-c__drop'), cat = bk && bk.querySelector('.clp-c__cat'), sig = bk && bk.querySelector('.clp-c__sig'), cta = bk && bk.querySelector('.clp-cta--d');
    if (!cd || !drop || !cat || !sig || !cta) return;
    sig.style.marginTop = '';
    if (MOBILE.matches) { bk.style.removeProperty('--cd-top'); return; }
    // positions de mise en page (offsetTop : insensibles aux animations d'entrée qui déplacent les textes)
    const gap = 120;                                                 // écart titre → DROP I = DROP I → compte à rebours (120 px)
    const dTop = drop.offsetTop, dBot = dTop + drop.offsetHeight, catBot = cat.offsetTop + cat.offsetHeight;
    sig.style.marginTop = Math.max(0, dTop - gap - catBot).toFixed(1) + 'px';   // descend signature + titre
    const room = bk.clientHeight - cta.offsetHeight - 12 - cd.offsetHeight - dBot;   // ne jamais toucher les boutons (12 px mini)
    bk.style.setProperty('--cd-top', (dBot + Math.max(14, Math.min(gap, room))).toFixed(1) + 'px');
  }
  if (bk && 'ResizeObserver' in window) new ResizeObserver(placeCountdown).observe(bk);
  if (document.fonts) document.fonts.ready.then(placeCountdown);
  MOBILE.addEventListener('change', placeCountdown);
  placeCountdown();

  // Lien « Me prévenir de la sortie » : ouvre le champ e-mail (sans JS, le champ est affiché d'emblée).
  sec.querySelectorAll('.clp-cta--notify').forEach((cta) => {
    const open = cta.querySelector('.clp-cta__open');
    if (!open || cta.querySelector('.clp-cta__err')) return;
    cta.classList.add('is-closed');
    open.addEventListener('click', () => {
      cta.classList.remove('is-closed');
      const input = cta.querySelector('input[type=email]');
      if (input) input.focus();
    });
  });
}

function boot(scope) {
  scope.querySelectorAll('.clp-drop:not([data-clp-drop-on])').forEach((sec) => {
    sec.setAttribute('data-clp-drop-on', '');
    try { initDrop(sec); } catch (e) { console.error('[drop]', e); }
  });
}
if (document.readyState === 'loading') addEventListener('DOMContentLoaded', () => boot(document), { once: true }); else boot(document);
document.addEventListener('shopify:section:load', (e) => boot(e.target));
